// Figma: "Loyalty Cloud" file, node 18335:1085 ("Container" — resident detail slide-over,
// opened from a row click in ./ResidentsTable.js). Reuses the same row shape ResidentsTable
// already prepares (prepareRow) plus prev/next navigation across the currently loaded rows —
// no separate "get resident by id" endpoint exists yet, so this drawer is fed the already-
// fetched row list + selected index rather than issuing its own fetch.
import React from 'react';
import PropTypes from 'prop-types';
import {Box, ButtonBase, CircularProgress, Drawer, Stack, Typography} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import {tokens} from '../../../themes/ThemeCustomization';
import {
    useCreateResidentTransactionsMutation,
    useGetResidentTransactionsQuery,
    useGetResidentPaymentHistoriesQuery
} from '../../../features/coreApi/residentsSlice';
import {formatOtherOffers} from '../Offers/utils';

import closeIcon from '../../../assets/residentDetail/close-icon.svg';
import chevronUpIcon from '../../../assets/residentDetail/chevron-up-icon.svg';
import chevronDownIcon from '../../../assets/residentDetail/chevron-down-icon.svg';
import avatarPlaceholderIcon from '../../../assets/residentDetail/avatar-placeholder-icon.svg';
import copyLinkIcon from '../../../assets/residentDetail/copy-link-icon.svg';
import copyIcon from '../../../assets/residentDetail/copy-icon.svg';
import transactionCreditIcon from '../../../assets/residentDetail/transaction-credit-icon.svg';
import transactionDebitIcon from '../../../assets/residentDetail/transaction-debit-icon.svg';
import EditResidentDialog from './EditResidentDialog';
import NewTransactionDialog, {TRANSACTION_TYPES} from './NewTransactionDialog';
import CommunicationsPanel from './CommunicationsPanel';
import AuthToast from '../../authentication/components/AuthToast';

const TABS = ['Details', 'Offers', 'Communications', 'Rent payments', 'Transactions'];

const DETAIL_FIELDS = [
    {label: 'Email', field: 'email', link: true, copyable: true},
    {label: 'Phone', field: 'phone', link: true, copyable: true},
    {label: 'Community', field: 'community'},
    {label: 'Campaign', field: 'campaign'},
    {label: 'Unit', field: 'unit'},
    {label: 'Floor plan', field: 'floorPlan'},
    {label: 'Rent', field: 'rent', render: (row) => row.rentDisplay},
    {label: 'Ledger balance', field: 'balance', render: (row) => row.balanceDisplay},
    {label: 'Lease start date', field: 'leaseStart', render: (row) => row.leaseStartDisplay},
    {label: 'Lease end date', field: 'leaseEnd', render: (row) => row.leaseEndDisplay},
    {label: 'Move in date', field: 'moveInDate', render: (row) => row.moveInDateDisplay},
    {label: 'Stake Checking', field: 'stakeChecking', render: (row) => row.stakeCheckingDisplay},
    {label: 'Virtual card', field: 'virtualCard'},
    {label: 'Physical card', field: 'physicalCard'},
    {label: 'Created at', field: 'createdAt', render: (row) => row.createdAtDisplay},
    {label: 'Notes', field: 'notes'}
];

const CopyButton = ({value}) => {
    const [status, setStatus] = React.useState('idle'); // idle | loading | copied

    const handleCopy = async () => {
        if (status !== 'idle' || !value) return;
        setStatus('loading');
        try {
            await navigator.clipboard?.writeText(value);
        } finally {
            setStatus('copied');
            setTimeout(() => setStatus('idle'), 1500);
        }
    };

    return (
        <ButtonBase
            onClick={handleCopy}
            sx={{
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                flexShrink: 0
            }}
        >
            {status === 'loading' ? (
                <CircularProgress size={14} thickness={5} sx={{color: tokens.color.neutral500}} />
            ) : status === 'copied' ? (
                <CheckCircleIcon sx={{fontSize: 16, color: tokens.color.success500}} />
            ) : (
                <Box component="img" src={copyIcon} alt="Copy" sx={{width: 14, height: 14}} />
            )}
        </ButtonBase>
    );
};

CopyButton.propTypes = {value: PropTypes.string};

const DetailRow = ({label, value, link, copyable}) => (
    <Stack direction="row" alignItems="center" sx={{minHeight: 32, py: '8px', gap: '8px'}}>
        <Typography variant="body2" sx={{width: '35%', color: tokens.color.neutral600}}>
            {label}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{flex: 1}}>
            <Typography
                variant="body2"
                sx={{
                    color: link ? tokens.color.sky600 : tokens.color.neutral950,
                    textDecoration: link ? 'underline' : 'none',
                    wordBreak: 'break-word'
                }}
            >
                {value || '-'}
            </Typography>
            {copyable && value && <CopyButton value={value} />}
        </Stack>
    </Stack>
);

DetailRow.propTypes = {label: PropTypes.string.isRequired, value: PropTypes.node, link: PropTypes.bool, copyable: PropTypes.bool};

const formatCurrency = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? `$${Math.abs(num).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '-';
};

const formatTransactionAmount = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    return `${num < 0 ? '-' : '+'}${formatCurrency(num)}`;
};

const formatGroupDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, {year: 'numeric', month: 'long', day: 'numeric'});
};

// `time` comes as a bare 24-hour "HH:mm" string (e.g. "13:40"), not a parseable timestamp.
const formatTransactionTime = (value) => {
    if (!value) return '';
    const [hours, minutes] = String(value).split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return '';
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date.toLocaleTimeString(undefined, {hour: 'numeric', minute: '2-digit'});
};

// Confirmed response shape (resident/{id}/transactions/all/): {data: {activities: [...],
// unclaimedTransactions: [...], currentBalance, unclaimedBalance, lifetimeEarnings, ...}}.
// `activities` entries carry direction/category/date/time/balance/status; unclaimed entries
// (also surfaced separately in `unclaimedTransactions`) only carry amount/date/category since
// they haven't posted yet — no time, running balance, or status to show.
const DIRECTION_CREDIT = new Set(['credit', 'unclaimed']);

const getTransactionFields = (transaction) => {
    const label = transaction.category || transaction.description || transaction.transaction_type || transaction.type || '-';
    const amount = Number(transaction.amount);
    const date = transaction.date || transaction.createdAt || transaction.date_dt;
    const time = transaction.time;
    const direction = String(transaction.direction || '').toLowerCase();
    const status = direction === 'unclaimed' ? 'Unclaimed' : transaction.status?.trim() || null;
    const method = transaction.stakeDebitType || transaction.method;
    const runningBalance = transaction.balance;
    const isCredit = direction ? DIRECTION_CREDIT.has(direction) : Number.isFinite(amount) && amount >= 0;

    return {label: String(label).replace(/_/g, ' '), amount, date, time, status, method, runningBalance, isCredit};
};

const STATUS_COLOR = {
    failed: tokens.color.error500,
    declined: tokens.color.error500
};

const TransactionRow = ({transaction}) => {
    const {label, amount, time, status, method, runningBalance, isCredit} = getTransactionFields(transaction);
    const statusColor = STATUS_COLOR[String(status).toLowerCase()] || tokens.color.neutral600;
    const subtitleParts = [formatTransactionTime(time), method].filter(Boolean);

    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{py: 1}}>
            <Box
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '999px',
                    border: `1px solid ${tokens.color.neutral200}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}
            >
                <Box component="img" src={isCredit ? transactionCreditIcon : transactionDebitIcon} alt="" sx={{width: 20, height: 20}} />
            </Box>
            <Stack sx={{flex: 1, minWidth: 0}}>
                <Typography variant="body2" sx={{color: tokens.color.neutral950, textTransform: 'capitalize'}} noWrap>
                    {label}
                </Typography>
                <Typography variant="body2" sx={{color: tokens.color.neutral600}} noWrap>
                    {subtitleParts.join(' | ')}
                </Typography>
            </Stack>
            <Stack alignItems="flex-end" sx={{flexShrink: 0}}>
                <Typography variant="body2" sx={{color: tokens.color.neutral950}}>
                    {formatTransactionAmount(amount)}
                </Typography>
                <Typography variant="body2" sx={{color: statusColor}}>
                    {status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : formatCurrency(runningBalance)}
                </Typography>
            </Stack>
        </Stack>
    );
};

TransactionRow.propTypes = {transaction: PropTypes.object.isRequired};

const PENDING_STATUSES = new Set(['pending', 'unclaimed']);

const groupTransactions = (transactions) => {
    const pending = [];
    const byDate = new Map();

    transactions.forEach((transaction) => {
        const {date, status} = getTransactionFields(transaction);
        if (status && PENDING_STATUSES.has(String(status).toLowerCase())) {
            pending.push(transaction);
            return;
        }
        const key = formatGroupDate(date);
        if (!byDate.has(key)) byDate.set(key, []);
        byDate.get(key).push(transaction);
    });

    const groups = Array.from(byDate.entries()).map(([title, items]) => ({title, items}));
    if (pending.length) groups.unshift({title: 'Unclaimed & Pending', items: pending});
    return groups;
};

const TransactionGroup = ({title, items}) => (
    <Stack sx={{width: '100%'}}>
        <Typography variant="body2" fontWeight={600} sx={{color: tokens.color.neutral950, py: 0.5}}>
            {title}
        </Typography>
        <Stack divider={<Box sx={{height: '1px', backgroundColor: tokens.color.neutral100}} />}>
            {items.map((transaction, index) => (
                <TransactionRow key={transaction.id || transaction.sfid || index} transaction={transaction} />
            ))}
        </Stack>
    </Stack>
);

TransactionGroup.propTypes = {title: PropTypes.string.isRequired, items: PropTypes.array.isRequired};

// Confirmed response has no separate "available balance" field — Available balance and
// Current balance both read from `currentBalance` (matches the Figma design, where the two
// show the same figure).
const BALANCE_FIELDS = [
    {label: 'Available balance', field: 'currentBalance'},
    {label: 'Current balance', field: 'currentBalance'},
    {label: 'Unclaimed balance', field: 'unclaimedBalance'},
    {label: 'Lifetime Earnings', field: 'lifetimeEarnings'}
];

const BalancesSummary = ({summary}) => (
    <Stack sx={{width: '100%'}}>
        <Typography variant="body2" sx={{color: tokens.color.neutral600, py: 0.5}}>
            Balances
        </Typography>
        <Stack direction="row" sx={{border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.control, overflow: 'hidden'}}>
            {BALANCE_FIELDS.map(({label, field}, index) => (
                <Box
                    key={label}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        px: 1.25,
                        py: 1.5,
                        borderLeft: index === 0 ? 'none' : `1px solid ${tokens.color.neutral200}`
                    }}
                >
                    <Typography
                        variant="caption"
                        noWrap
                        sx={{color: tokens.color.neutral600, textDecoration: 'underline', textDecorationStyle: 'dotted', display: 'block'}}
                    >
                        {label}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{color: tokens.color.neutral950}}>
                        {formatCurrency(summary?.[field])}
                    </Typography>
                </Box>
            ))}
        </Stack>
    </Stack>
);

BalancesSummary.propTypes = {summary: PropTypes.object};

const TransactionsPanel = ({mobileUserId}) => {
    const {data, isLoading, isFetching, isError} = useGetResidentTransactionsQuery(mobileUserId, {skip: !mobileUserId});
    // Confirmed shape: {activities: [...], unclaimedTransactions: [...], currentBalance,
    // unclaimedBalance, lifetimeEarnings, ...}. Unclaimed items aren't in `activities`, so
    // both lists are combined before grouping — direction "Unclaimed" routes them into the
    // "Unclaimed & Pending" group via getTransactionFields/groupTransactions.
    const transactions = [...(data?.activities || []), ...(data?.unclaimedTransactions || [])];
    const summary = data;

    if (!mobileUserId) {
        return (
            <Typography variant="body2" sx={{color: tokens.color.neutral500, py: 4, textAlign: 'center'}}>
                No transaction record available for this resident.
            </Typography>
        );
    }

    if (isLoading || isFetching) {
        return (
            <Stack alignItems="center" sx={{py: 4}}>
                <CircularProgress size={20} thickness={5} sx={{color: tokens.color.neutral500}} />
            </Stack>
        );
    }

    if (isError) {
        return (
            <Typography variant="body2" sx={{color: tokens.color.neutral500, py: 4, textAlign: 'center'}}>
                Unable to load transactions. Please try again later.
            </Typography>
        );
    }

    const groups = groupTransactions(transactions);

    return (
        <Stack spacing={2}>
            <BalancesSummary summary={summary} />
            <Stack spacing={1.5}>
                <Typography variant="body2" sx={{color: tokens.color.neutral600, py: 0.5}}>
                    Transactions
                </Typography>
                {groups.length ? (
                    <Stack spacing={2}>
                        {groups.map((group) => (
                            <TransactionGroup key={group.title} title={group.title} items={group.items} />
                        ))}
                    </Stack>
                ) : (
                    <Typography variant="body2" sx={{color: tokens.color.neutral500, py: 4, textAlign: 'center'}}>
                        No transactions yet.
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
};

TransactionsPanel.propTypes = {mobileUserId: PropTypes.string};

// Figma: "Loyalty Cloud" file, node 18428:74323 — "Offer conditions" | "Reward" 2-column
// table. `formatOtherOffers` (ported in ../Offers/utils.js) already produces both columns'
// text from a raw offer object: `offerDeterminersValues` (the "Apply between…Move-in
// between…" lines) and `offerRewardChipValue` ("100% move-in bonus", "3% Cash Back", etc).
const OffersPanel = ({offers}) => {
    const formattedOffers = (offers || []).filter((offer) => offer.reward_amount > 0).map((offer) => formatOtherOffers(offer));

    if (!formattedOffers.length) {
        return (
            <Typography variant="body2" sx={{color: tokens.color.neutral500, py: 4, textAlign: 'center'}}>
                No offers for this resident.
            </Typography>
        );
    }

    return (
        <Stack
            sx={{
                border: `1px solid ${tokens.color.neutral200}`,
                borderRadius: tokens.radius.control,
                overflow: 'hidden'
            }}
        >
            <Stack direction="row">
                <Box sx={{flex: 1.35, px: 2, py: 1.5, borderBottom: `1px solid ${tokens.color.neutral200}`, borderRight: `1px solid ${tokens.color.neutral200}`}}>
                    <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.neutral600}}>
                        Offer conditions
                    </Typography>
                </Box>
                <Box sx={{flex: 0.75, px: 2, py: 1.5, borderBottom: `1px solid ${tokens.color.neutral200}`}}>
                    <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.neutral600}}>
                        Reward
                    </Typography>
                </Box>
            </Stack>
            {formattedOffers.map((offer, index) => {
                const isLast = index === formattedOffers.length - 1;
                return (
                    <Stack direction="row" key={offer.id || index}>
                        <Box
                            sx={{
                                flex: 1.35,
                                px: 2,
                                py: 1.5,
                                borderRight: `1px solid ${tokens.color.neutral200}`,
                                borderBottom: isLast ? 'none' : `1px solid ${tokens.color.neutral200}`
                            }}
                        >
                            {offer.offerDeterminersValues.length ? (
                                offer.offerDeterminersValues.map((line, lineIndex) => (
                                    <Typography key={lineIndex} variant="body2" sx={{color: tokens.color.neutral950}}>
                                        {line}
                                    </Typography>
                                ))
                            ) : (
                                <Typography variant="body2" sx={{color: tokens.color.neutral950}}>
                                    -
                                </Typography>
                            )}
                        </Box>
                        <Box
                            sx={{
                                flex: 0.75,
                                px: 2,
                                py: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                borderBottom: isLast ? 'none' : `1px solid ${tokens.color.neutral200}`
                            }}
                        >
                            <Typography variant="body2" sx={{color: tokens.color.neutral950}} noWrap>
                                {offer.offerRewardChipValue}
                            </Typography>
                        </Box>0
                    </Stack>
                );
            })}
        </Stack>
    );
};

OffersPanel.propTypes = {offers: PropTypes.array};

// Figma: "Loyalty Cloud" file, node 18428:69730 — "Rent payments" tab, a flat table of
// Month | Paid on time | Paid with Stake Checking, one row per month, each status cell a
// green check / red X / "-" (no data for that month yet, e.g. the current month).
const formatPaymentMonth = (year, month) => {
    if (!year || !month) return '-';
    const date = new Date(Number(year), Number(month) - 1, 1);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString(undefined, {year: 'numeric', month: 'short'});
};

// `year__c`/`month__c` (separate fields, e.g. "2025"/"2") plus `paid_on_time__c` /
// `paid_via_svd__c` are the actual Salesforce fields returned by resident_payment_histories.
const getPaymentHistoryFields = (record) => ({
    month: formatPaymentMonth(record.year__c, record.month__c),
    sortKey: Number(record.year__c) * 12 + Number(record.month__c),
    paidOnTime: record.paid_on_time__c,
    paidWithStakeChecking: record.paid_via_svd__c
});

const PaymentStatusCell = ({value}) => {
    if (value === null || value === undefined) {
        return (
            <Typography variant="body2" sx={{color: tokens.color.neutral950}}>
                -
            </Typography>
        );
    }
    return value ? (
        <CheckCircleIcon sx={{fontSize: 18, color: tokens.color.success500}} />
    ) : (
        <CancelIcon sx={{fontSize: 18, color: tokens.color.error500}} />
    );
};

PaymentStatusCell.propTypes = {value: PropTypes.bool};

const RentPaymentsPanel = ({mobileUserId}) => {
    const {data, isLoading, isFetching, isError} = useGetResidentPaymentHistoriesQuery(mobileUserId, {skip: !mobileUserId});
    const paymentHistories = React.useMemo(() => {
        const list = Array.isArray(data) ? data : data?.results || [];
        return [...list].sort((a, b) => getPaymentHistoryFields(b).sortKey - getPaymentHistoryFields(a).sortKey);
    }, [data]);

    if (!mobileUserId) {
        return (
            <Typography variant="body2" sx={{color: tokens.color.neutral500, py: 4, textAlign: 'center'}}>
                No rent payment record available for this resident.
            </Typography>
        );
    }

    if (isLoading || isFetching) {
        return (
            <Stack alignItems="center" sx={{py: 4}}>
                <CircularProgress size={20} thickness={5} sx={{color: tokens.color.neutral500}} />
            </Stack>
        );
    }

    if (isError) {
        return (
            <Typography variant="body2" sx={{color: tokens.color.neutral500, py: 4, textAlign: 'center'}}>
                Unable to load rent payments. Please try again later.
            </Typography>
        );
    }

    if (!paymentHistories.length) {
        return (
            <Typography variant="body2" sx={{color: tokens.color.neutral500, py: 4, textAlign: 'center'}}>
                No rent payments for this resident.
            </Typography>
        );
    }

    return (
        <Stack sx={{border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.control, overflow: 'hidden'}}>
            <Stack direction="row">
                <Box sx={{flex: 0.65, px: 2, py: 1.5, borderBottom: `1px solid ${tokens.color.neutral200}`, borderRight: `1px solid ${tokens.color.neutral200}`}} />
                <Box sx={{flex: 1, px: 2, py: 1.5, borderBottom: `1px solid ${tokens.color.neutral200}`, borderRight: `1px solid ${tokens.color.neutral200}`}}>
                    <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.neutral600}}>
                        Paid on time
                    </Typography>
                </Box>
                <Box sx={{flex: 1, px: 2, py: 1.5, borderBottom: `1px solid ${tokens.color.neutral200}`}}>
                    <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.neutral600}}>
                        Paid with Stake Checking
                    </Typography>
                </Box>
            </Stack>
            {paymentHistories.map((record, index) => {
                const {month, paidOnTime, paidWithStakeChecking} = getPaymentHistoryFields(record);
                const isLast = index === paymentHistories.length - 1;
                const borderBottom = isLast ? 'none' : `1px solid ${tokens.color.neutral200}`;
                return (
                    <Stack direction="row" key={record.sfid || record.id || index}>
                        <Box sx={{flex: 0.65, px: 2, py: 1.5, borderRight: `1px solid ${tokens.color.neutral200}`, borderBottom}}>
                            <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.neutral950}}>
                                {month}
                            </Typography>
                        </Box>
                        <Box sx={{flex: 1, px: 2, py: 1.5, display: 'flex', alignItems: 'center', borderRight: `1px solid ${tokens.color.neutral200}`, borderBottom}}>
                            <PaymentStatusCell value={paidOnTime} />
                        </Box>
                        <Box sx={{flex: 1, px: 2, py: 1.5, display: 'flex', alignItems: 'center', borderBottom}}>
                            <PaymentStatusCell value={paidWithStakeChecking} />
                        </Box>
                    </Stack>
                );
            })}
        </Stack>
    );
};

RentPaymentsPanel.propTypes = {mobileUserId: PropTypes.string};

const ResidentDetailDrawer = ({open, onClose, rows, selectedIndex, onNavigate}) => {
    const resident = selectedIndex != null ? rows[selectedIndex] : null;
    const total = rows.length;
    const hasPrev = selectedIndex > 0;
    const hasNext = selectedIndex != null && selectedIndex < total - 1;
    const [activeTab, setActiveTab] = React.useState('Details');
    const [editOpen, setEditOpen] = React.useState(false);
    const [editSaving, setEditSaving] = React.useState(false);
    const [transactionOpen, setTransactionOpen] = React.useState(false);
    const [toast, setToast] = React.useState(null);
    const [createResidentTransaction, {isLoading: transactionSubmitting}] = useCreateResidentTransactionsMutation();

    if (!resident) return null;

    // Stubbed until the real updateResidentInfo API + its error responses are confirmed —
    // see EditResidentDialog.js's header comment. The 'duplicate@test.com' /
    // '+1 (415) 738-2104' / 'servererror@test.com' sentinel values let the error toasts be
    // exercised before that API contract exists; swap this check for the real
    // success/error handling (e.g. a .catch() on the mutation) once it's wired up.
    const handleEditSave = ({email, phone} = {}) => {
        setEditSaving(true);
        setTimeout(() => {
            setEditSaving(false);

            if (email?.trim().toLowerCase() === 'duplicate@test.com') {
                setToast({
                    title: 'Email unavailable',
                    description: `The email address ${email.trim()} is already associated with another account.`,
                    variant: 'error'
                });
                return;
            }
            if (phone?.replace(/\D/g, '') === '14157382104') {
                setToast({
                    title: 'Phone number unavailable',
                    description: `The phone number ${phone.trim()} is already associated with another account.`,
                    variant: 'error'
                });
                return;
            }
            if (email?.trim().toLowerCase() === 'servererror@test.com') {
                setToast({title: 'Unable to save', description: 'Something went wrong on our end. Please try again later.', variant: 'error'});
                return;
            }

            setEditOpen(false);
            setToast({title: 'Changes saved', description: 'Resident details have been updated successfully.', variant: 'success'});
        }, 1200);
    };

    // Figma: "Toast" node 18428:73607 — "{Type} of ${amount} has been applied to {resident}'s account."
    const handleTransactionSubmit = async ({transactionType, amount} = {}) => {
        const typeLabel = TRANSACTION_TYPES.find((type) => type.value === transactionType)?.label || transactionType;

        try {
            await createResidentTransaction({id: resident.mobileUserId, amount: Number(amount), description: typeLabel}).unwrap();
            setTransactionOpen(false);
            setToast({
                title: 'Transaction complete',
                description: `${typeLabel} of ${formatCurrency(amount)} has been applied to ${resident.name}'s account.`,
                variant: 'success'
            });
        } catch (error) {
            setToast({title: 'Unable to submit', description: 'Something went wrong on our end. Please try again later.', variant: 'error'});
        }
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{sx: {width: '560px'}}}>
            <Stack sx={{height: '100%'}}>
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{height: '57px', px: 2, borderBottom: `1px solid ${tokens.color.neutral200}`, flexShrink: 0}}
                >
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{flex: 1}}>
                        <ButtonBase onClick={onClose} sx={{p: 1, borderRadius: '8px'}}>
                            <Box component="img" src={closeIcon} alt="Close" sx={{width: 16, height: 16}} />
                        </ButtonBase>
                        <Stack direction="row" spacing={0.75}>
                            <ButtonBase
                                disabled={!hasPrev}
                                onClick={() => onNavigate(selectedIndex - 1)}
                                sx={{
                                    p: 1,
                                    border: `0.8px solid ${tokens.color.neutral200}`,
                                    borderRadius: tokens.radius.control,
                                    backgroundColor: tokens.color.white,
                                    opacity: hasPrev ? 1 : 0.4
                                }}
                            >
                                <Box component="img" src={chevronUpIcon} alt="Previous record" sx={{width: 16, height: 16}} />
                            </ButtonBase>
                            <ButtonBase
                                disabled={!hasNext}
                                onClick={() => onNavigate(selectedIndex + 1)}
                                sx={{
                                    p: 1,
                                    border: `0.8px solid ${tokens.color.neutral200}`,
                                    borderRadius: tokens.radius.control,
                                    backgroundColor: tokens.color.white,
                                    opacity: hasNext ? 1 : 0.4
                                }}
                            >
                                <Box component="img" src={chevronDownIcon} alt="Next record" sx={{width: 16, height: 16}} />
                            </ButtonBase>
                        </Stack>
                        <Typography variant="caption" sx={{color: tokens.color.neutral500}}>
                            {selectedIndex + 1} of {total} Residents
                        </Typography>
                    </Stack>
                    <ButtonBase
                        onClick={() => navigator.clipboard?.writeText(window.location.href)}
                        sx={{
                            height: 32,
                            px: 1.5,
                            border: `1px solid ${tokens.color.neutral200}`,
                            borderRadius: tokens.radius.control,
                            backgroundColor: tokens.color.white,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <Box component="img" src={copyLinkIcon} alt="" sx={{width: 14, height: 14}} />
                        <Typography variant="caption" fontWeight={500}>
                            Copy link
                        </Typography>
                    </ButtonBase>
                </Stack>

                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{p: 1.5, borderBottom: `1px solid ${tokens.color.neutral200}`, flexShrink: 0}}
                >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: tokens.radius.control,
                                border: `1px solid ${tokens.color.neutral200}`,
                                backgroundColor: tokens.color.neutral50,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden'
                            }}
                        >
                            <Box component="img" src={avatarPlaceholderIcon} alt="" sx={{width: 32, height: 32}} />
                        </Box>
                        <Typography variant="h6" fontWeight={600} sx={{fontSize: '16px'}}>
                            {resident.name}
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <ButtonBase
                            onClick={() => setEditOpen(true)}
                            sx={{
                                height: 32,
                                px: 1.5,
                                border: `1px solid ${tokens.color.neutral200}`,
                                borderRadius: tokens.radius.control,
                                backgroundColor: tokens.color.white
                            }}
                        >
                            <Typography variant="body2" fontWeight={500}>
                                Edit
                            </Typography>
                        </ButtonBase>
                        <ButtonBase
                            onClick={() => setTransactionOpen(true)}
                            sx={{
                                height: 32,
                                px: 1.5,
                                borderRadius: tokens.radius.control,
                                backgroundColor: tokens.color.sky600
                            }}
                        >
                            <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.white}}>
                                New transaction
                            </Typography>
                        </ButtonBase>
                    </Stack>
                </Stack>

                <Stack direction="row" spacing={0.5} sx={{px: 1.5, pt: 1.5, pb: 1, borderBottom: `1px solid ${tokens.color.neutral200}`, flexShrink: 0}}>
                    {TABS.map((tab) => (
                        <ButtonBase
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            sx={{
                                position: 'relative',
                                px: 1,
                                py: 0.5,
                                borderRadius: '8px',
                                gap: 0.75,
                                flexShrink: 0,
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{color: activeTab === tab ? tokens.color.neutral950 : tokens.color.neutral600, whiteSpace: 'nowrap'}}
                            >
                                {tab}
                            </Typography>
                            {tab === 'Offers' && resident.offers > 0 && (
                                <Box
                                    sx={{
                                        height: 14,
                                        px: 0.5,
                                        borderRadius: '4px',
                                        border: `0.5px solid ${tokens.color.neutral200}`,
                                        backgroundColor: tokens.color.neutral100,
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    <Typography sx={{fontSize: '10px', fontWeight: 500, color: tokens.color.neutral600}}>{resident.offers}</Typography>
                                </Box>
                            )}
                            {activeTab === tab && (
                                <Box sx={{position: 'absolute', left: 0, right: 0, bottom: '-9px', height: '1px', backgroundColor: tokens.color.neutral950}} />
                            )}
                        </ButtonBase>
                    ))}
                </Stack>

                <Box sx={{flex: 1, overflow: 'auto', px: 2, py: 1.5}}>
                    {activeTab === 'Details' ? (
                        <Stack>
                            {DETAIL_FIELDS.map(({label, field, link, render, copyable}) => (
                                <DetailRow
                                    key={field}
                                    label={label}
                                    value={render ? render(resident) : resident[field]}
                                    link={link}
                                    copyable={copyable}
                                />
                            ))}
                        </Stack>
                    ) : activeTab === 'Offers' ? (
                        <OffersPanel offers={resident.rawOffers} />
                    ) : activeTab === 'Rent payments' ? (
                        <RentPaymentsPanel mobileUserId={resident.mobileUserId} />
                    ) : activeTab === 'Transactions' ? (
                        <TransactionsPanel mobileUserId={resident.mobileUserId} />
                    ) : activeTab === 'Communications' ? (
                        <CommunicationsPanel resident={resident} />
                    ) : (
                        <Typography variant="body2" sx={{color: tokens.color.neutral500, py: 4, textAlign: 'center'}}>
                            {activeTab} coming soon.
                        </Typography>
                    )}
                </Box>
            </Stack>

            <EditResidentDialog
                open={editOpen}
                resident={resident}
                saving={editSaving}
                onClose={() => setEditOpen(false)}
                onSave={handleEditSave}
            />

            <NewTransactionDialog
                open={transactionOpen}
                resident={resident}
                submitting={transactionSubmitting}
                onClose={() => setTransactionOpen(false)}
                onSubmit={handleTransactionSubmit}
            />

            <AuthToast toast={toast} onClose={() => setToast(null)} />
        </Drawer>
    );
};

ResidentDetailDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    rows: PropTypes.array.isRequired,
    selectedIndex: PropTypes.number,
    onNavigate: PropTypes.func.isRequired
};

export default ResidentDetailDrawer;
